import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ImagePlus } from 'lucide-react';
import { programApi } from '@/api/program';
import { useCreateProgram, useProgram, useUpdateProgram } from '@/hooks/useProgram';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { notifySuccess } from '@/lib/feedback';

function formatCurrencyInput(value: string) {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';

  return Number(digits).toLocaleString('id-ID');
}

function parseCurrencyInput(value: string) {
  return value.replace(/\D/g, '');
}

export function ProgramEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const programId = Number(id);
  const isEdit = Number.isFinite(programId) && programId > 0;
  const initialJenisProgram = searchParams.get('jenis') === 'khusus' ? 'khusus' : 'utama';

  const { data, isLoading } = useProgram(programId);
  const createProgram = useCreateProgram();
  const updateProgram = useUpdateProgram();

  const [nama, setNama] = useState('');
  const [deskripsi, setDeskripsi] = useState('');
  const [tanggalMulai, setTanggalMulai] = useState('');
  const [tanggalSelesai, setTanggalSelesai] = useState('');
  const [targetDonasi, setTargetDonasi] = useState('');
  const [status, setStatus] = useState('active');
  const [jenisProgram, setJenisProgram] = useState<'utama' | 'khusus'>(initialJenisProgram);
  const [kategoriProgram, setKategoriProgram] = useState('kesehatan');
  const [coverPreview, setCoverPreview] = useState('');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit || !data) return;
    setNama(data.nama || '');
    setDeskripsi(data.deskripsi || '');
    setTanggalMulai(data.tanggalMulai || '');
    setTanggalSelesai(data.tanggalSelesai || '');
    setTargetDonasi(data.targetDonasi ? formatCurrencyInput(String(data.targetDonasi)) : '');
    setStatus(data.status || 'active');
    setJenisProgram(data.jenisProgram || initialJenisProgram);
    setKategoriProgram(data.kategoriProgram || 'kesehatan');
    setCoverPreview(data.cover || '');
  }, [isEdit, data, initialJenisProgram]);

  const pageTitle = useMemo(() => (isEdit ? 'Ubah Program' : 'Buat Program'), [isEdit]);
  const busy = createProgram.isPending || updateProgram.isPending;

  const onPickCover = (file?: File) => {
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const saveProgram = async () => {
    setError('');
    if (!nama.trim()) {
      setError('Nama program wajib diisi.');
      return;
    }

    if (jenisProgram === 'utama' && !kategoriProgram) {
      setError('Kategori program utama wajib dipilih.');
      return;
    }

    if (jenisProgram === 'khusus' && !tanggalSelesai) {
      setError('Tanggal selesai wajib diisi untuk program khusus.');
      return;
    }

    try {
      let coverUrl = (isEdit ? (data?.cover || '') : '').trim();

      if (coverFile) {
        const uploaded = await programApi.uploadCover(coverFile);
        coverUrl = uploaded.url || '';
        notifySuccess('Gambar program berhasil diunggah');
      }

      if (!coverUrl) {
        setError('Gambar program wajib diupload.');
        return;
      }

      const payload = {
        nama: nama.trim(),
        deskripsi: deskripsi || undefined,
        tanggalMulai: tanggalMulai || undefined,
        tanggalSelesai: jenisProgram === 'khusus' ? (tanggalSelesai || undefined) : null,
        jenisProgram,
        kategoriProgram: jenisProgram === 'utama' ? kategoriProgram : null,
        targetDonasi: targetDonasi ? Number(parseCurrencyInput(targetDonasi)) : undefined,
        status,
        cover: coverUrl,
      };

      if (isEdit) {
        await updateProgram.mutateAsync({ id: programId, data: payload });
      } else {
        await createProgram.mutateAsync(payload);
      }

      navigate('/program');
    } catch (error) {
      console.error('Failed to save program:', error);
    }
  };

  if (isEdit && isLoading) {
    return <div>Memuat...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="mb-2">
            <Link to="/program" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              Kembali ke daftar program
            </Link>
          </div>
          <h2 className="text-3xl font-bold tracking-tight">{pageTitle}</h2>
          <p className="text-muted-foreground">Program menggunakan halaman dedicated, bukan popup.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/program')}>Batal</Button>
          <Button onClick={saveProgram} disabled={busy}>
            {busy ? 'Menyimpan...' : 'Simpan Program'}
          </Button>
        </div>
      </div>

      {error ? <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}

      <Card>
        <CardHeader>
        <CardTitle>Informasi Program</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="status">
                  <SelectValue placeholder="Pilih status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="inactive">Tidak Aktif</SelectItem>
                  <SelectItem value="completed">Selesai</SelectItem>
                  <SelectItem value="cancelled">Dibatalkan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="jenisProgram">Jenis Program</Label>
              <Select value={jenisProgram} onValueChange={(value: 'utama' | 'khusus') => setJenisProgram(value)}>
                <SelectTrigger id="jenisProgram">
                  <SelectValue placeholder="Pilih jenis program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="utama">Program Utama</SelectItem>
                  <SelectItem value="khusus">Program Khusus</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {jenisProgram === 'utama' ? (
            <div className="space-y-2">
              <Label htmlFor="kategoriProgram">Kategori Program Utama</Label>
              <Select value={kategoriProgram} onValueChange={setKategoriProgram}>
                <SelectTrigger id="kategoriProgram">
                  <SelectValue placeholder="Pilih kategori program utama" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kesehatan">Kesehatan</SelectItem>
                  <SelectItem value="sosial">Sosial</SelectItem>
                  <SelectItem value="pendidikan">Pendidikan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="nama">Nama Program</Label>
            <Input id="nama" value={nama} onChange={(e) => setNama(e.target.value)} placeholder="Nama program" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="deskripsi">Deskripsi</Label>
            <Textarea id="deskripsi" value={deskripsi} onChange={(e) => setDeskripsi(e.target.value)} rows={4} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="tanggalMulai">Tanggal Mulai</Label>
              <Input id="tanggalMulai" type="date" value={tanggalMulai} onChange={(e) => setTanggalMulai(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tanggalSelesai">Tanggal Selesai</Label>
              <Input
                id="tanggalSelesai"
                type="date"
                value={tanggalSelesai}
                onChange={(e) => setTanggalSelesai(e.target.value)}
                disabled={jenisProgram !== 'khusus'}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="targetDonasi">Target Donasi (Rp)</Label>
              <Input
                id="targetDonasi"
                inputMode="numeric"
                value={targetDonasi}
                onChange={(e) => setTargetDonasi(formatCurrencyInput(e.target.value))}
                placeholder="Contoh: 10.000.000"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Gambar Program</CardTitle>
          <Label htmlFor="cover-upload" className="cursor-pointer">
            <span className="inline-flex items-center rounded-md border px-3 py-2 text-sm">
              <ImagePlus className="mr-2 h-4 w-4" />
              Upload Gambar
            </span>
          </Label>
          <Input
            id="cover-upload"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => onPickCover(e.target.files?.[0])}
          />
        </CardHeader>
        <CardContent>
          {coverPreview ? (
            <img src={coverPreview} alt="Cover program" className="h-56 w-full rounded-md object-cover" />
          ) : (
            <div className="flex h-56 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
              Belum ada gambar program
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
